import { DocumentData, QueryDocumentSnapshot, DocumentSnapshot } from "firebase/firestore";
import { MediaType } from "@/types/homepage";
import { getStorage, ref, uploadBytes, uploadBytesResumable, getDownloadURL } from "firebase/storage";

// Helper function to convert Firestore doc to typed data
export const convertDoc = <T>(
  docSnapshot: QueryDocumentSnapshot<DocumentData> | DocumentSnapshot<DocumentData>
): T => {
  if (!docSnapshot.exists()) {
    throw new Error(`Document does not exist!`);
  }
  return {
    id: docSnapshot.id,
    ...docSnapshot.data()
  } as unknown as T;
};

// Additional helper that includes the document ID explicitly
export const convertDocWithId = <T>(
  docSnapshot: QueryDocumentSnapshot<DocumentData> | DocumentSnapshot<DocumentData>
): T & { _docId: string } => {
  if (!docSnapshot.exists()) {
    throw new Error(`Document does not exist!`);
  }
  const data = {
    id: docSnapshot.id,
    _docId: docSnapshot.id,
    ...docSnapshot.data()
  };
  return data as unknown as T & { _docId: string };
};

// Helper function to check online status
export const isOnline = (): boolean => {
  return typeof navigator !== 'undefined' && navigator.onLine;
};

// Helper function to handle offline state
export const handleOfflineError = (error: any): never => {
  if (!isOnline()) {
    throw new Error('You are currently offline. Please check your internet connection and try again.');
  }
  throw error;
};

// Helper function to get image metadata
export const getImageMetadata = async (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height
      });
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

// Helper function to get video metadata
export const getVideoMetadata = async (file: File): Promise<{ width: number; height: number; duration: number }> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration
      });
    };
    video.onerror = reject;
    video.src = URL.createObjectURL(file);
  });
};

// Helper function to get file extension
export const getExtension = (filename: string): string => {
  return filename.split('.').pop()?.toLowerCase() || '';
};

export const getFileExtension = (filename: string): string => {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop()?.toLowerCase() || '' : '';
};

// Helper function to get media type from URL
export const getMediaTypeFromUrl = (url: string): MediaType => {
  const extension = getFileExtension(url);
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
  const videoExtensions = ['mp4', 'webm', 'ogg'];
  
  if (imageExtensions.includes(extension)) {
    return 'image';
  } else if (videoExtensions.includes(extension)) {
    return 'video';
  }
  return 'image'; // Default to image if extension is unknown
};

// Helper function to upload image with progress tracking
export const uploadImage = async (
  file: File, 
  path: string, 
  onProgress?: (progress: number) => void
): Promise<string> => {
  const storage = getStorage();
  const storageRef = ref(storage, path);
  
  if (onProgress) {
    // Use Firebase's uploadBytesResumable which supports progress tracking
    return new Promise((resolve, reject) => {
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      // Register observers for the upload task
      uploadTask.on(
        'state_changed',
        // Progress observer
        (snapshot) => {
          const progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          onProgress(progress);
        },
        // Error observer
        (error) => {
          console.error('Upload error:', error);
          reject(error);
        },
        // Completion observer
        async () => {
          try {
            const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadUrl);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  } else {
    // Fallback to regular upload without progress tracking
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  }
};
